import { Grid } from '@ui-propulsion/react-components';
import PropTypes from 'prop-types';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import parse from 'html-react-parser';
import classNames from 'classnames';
import styles from './benefitSection.module.scss';
import BenefitItem from './BenefitItem';
import CtaButton from '../ctaButton/ctaButton';
import Reveal from '../reveal/reveal';

// 2×2+1 card layout (5 cards: 2 rows of 2 + 1 centered below).
const GRID_2X2PLUS1 = 5;

const BenefitCarousel = dynamic(() => import('./benefitCarousel'), {
  ssr: false,
});

/*
Helper function to get column sizes based on the number of columns specified.
The 2×2+1 variant shares the 2-column size config; its 5th-card centering
is handled by the container's justifyContent="center".
@param {number} noOfColumns - Number of columns to display
@returns {object} - Column size configuration for different screen sizes
*/
const getColumnSizes = (noOfColumns) => {
  switch (noOfColumns) {
    case 1:
      return { xs: 12, sm: 12, md: 12, lg: 12 };
    case 2:
      return { xs: 12, sm: 6, md: 6, lg: 6 };
    case GRID_2X2PLUS1:
      return { xs: 12, sm: 12, md: 12, lg: 6 };
    case 3:
      return { xs: 12, sm: 6, md: 4, lg: 4 };
    default:
      return { xs: 12, sm: 6, md: 3, lg: 3 };
  }
};

/*
Helper function to render the headline and description section
@returns {JSX.Element} - Rendered headline and description
*/
const renderHeadlineDescription = ({
  headline,
  description,
  descPosition,
  textPosition,
  listProducts,
}) => (
  <>
    {!listProducts && headline && (
      <h2
        className={`${styles.title} ${
          styles[`textAlign-${textPosition}`] || ''
        }`}
      >
        {parse(headline)}
      </h2>
    )}
    {!listProducts && description && descPosition === 'top' && (
      <div
        className={`${styles.description} ${
          styles[`textAlign-${textPosition}`] || ''
        }`}
      >
        {parse(description)}
      </div>
    )}

    {listProducts && (
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={12} md={4} lg={4}>
          <div className={styles.titleContainer}>
            {headline && (
              <h2
                className={`${styles.title} ${styles.titleProducts} ${
                  styles[`textAlign-${textPosition}`] || ''
                }`}
              >
                {parse(headline)}
              </h2>
            )}
            {description && (
              <div
                className={`${styles.description} ${
                  styles.descriptionProducts
                } ${styles[`textAlign-${textPosition}`] || ''}`}
              >
                {parse(description)}
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    )}
  </>
);

/*
Main BenefitSection component
@component
@prop {string} headline - Section headline text
@prop {string} description - Section description text
@prop {string} descPosition - Position of the description text ('top', 'bottom')
@prop {object} infoCta - Object containing label and link for informational CTA
@prop {object} infoIcon - Object containing image path and alt text for informational icon
@prop {array} items - Array of benefit items to render
@prop {object} cta - Call-to-action object with label, link, and theme
@prop {number} noOfColumns - Number of columns in the grid layout; 5 selects the 2×2+1 variant
@prop {string} textPosition - Text alignment for content ('center', 'left')
@prop {boolean} listProducts - Boolean indicating if it is a product list
@prop {string} theme - Custom theme for styling the section
@prop {string} type - Card variant ('text' | 'icon' | 'noIcon' | 'illustration' | 'leftCheckIcon' | 'thumbnail')
@prop {boolean} mobileCarousel - Optional; when true, renders items as a horizontal Swiper carousel below 768px
@returns {JSX.Element} - Rendered BenefitSection component
*/
function BenefitSection({
  headline = '',
  description = '',
  descPosition = 'top',
  infoCta = null,
  infoIcon = null,
  items = [],
  cta = null,
  noOfColumns = 4,
  textPosition,
  listProducts = false,
  theme = null,
  type = 'illustration',
  mobileCarousel = false,
  enableAnimations = false,
}) {
  const is2x2Plus1 = noOfColumns === GRID_2X2PLUS1;
  const hasAtLeastOneImage = items.some((item) => item.image !== null);
  const columnSizes = getColumnSizes(noOfColumns);

  const sectionClassName = classNames(
    theme && styles[theme],
    styles.benefitSection,
    {
      [styles.benefitSectionProducts]: listProducts,
      [styles.benefitPadding]: !headline,
      [styles.sectionGrid2x2Plus1]: is2x2Plus1,
    }
  );

  const renderBenefitItem = (item) => (
    <BenefitItem
      {...item}
      hasAtLeastOneImage={hasAtLeastOneImage}
      textPosition={textPosition}
      listProducts={listProducts}
      type={type ?? 'illustration'}
    />
  );

  const gridClassName = classNames(
    { [styles.benefitGrid]: headline },
    { [styles.grid2x2Plus1]: is2x2Plus1 },
    { [styles.gridHiddenOnMobile]: mobileCarousel }
  );

  const gridCards = (
    <Grid
      container
      spacing={3}
      justifyContent="center"
      className={gridClassName}
    >
      {items.map((item, index) => (
        <Grid
          key={item.headline}
          item
          xs={columnSizes.xs}
          sm={columnSizes.sm}
          md={columnSizes.md}
          lg={columnSizes.lg}
          className={classNames({
            [styles.benefitGridItem]:
              textPosition === 'left' && type === 'leftCheckIcon',
          })}
        >
          <Reveal
            variant="up"
            delay={(index % noOfColumns) * 100}
            enabled={enableAnimations}
          >
            {renderBenefitItem(item)}
          </Reveal>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <div className={sectionClassName}>
      {renderHeadlineDescription({
        headline,
        description,
        descPosition,
        textPosition,
        listProducts,
      })}

      {mobileCarousel && (
        <BenefitCarousel
          items={items}
          renderItem={renderBenefitItem}
          className={classNames(
            styles.benefitGrid,
            styles.mobileCarousel,
            styles.carouselHiddenOnDesktop,
            { [styles.grid2x2Plus1]: is2x2Plus1 }
          )}
        />
      )}

      {gridCards}

      {cta && (
        <Grid
          item
          container
          xs={12}
          justifyContent="center"
          className={styles.ctaContainerButton}
        >
          <CtaButton
            label={cta.label}
            href={cta.link}
            variant={theme === 'transparent' ? 'Primary White' : cta.variant}
            color={cta.color}
          />
        </Grid>
      )}

      {infoCta && (
        <div className={styles.infoSection}>
          <Link href={infoCta.link}>
            {infoIcon && (
              <img src={infoIcon.path} alt={infoIcon.alt} loading="lazy" />
            )}
            <span className={styles.infoText}>{infoCta.label}</span>
          </Link>
        </div>
      )}
    </div>
  );
}

BenefitSection.propTypes = {
  headline: PropTypes.string,
  description: PropTypes.string,
  descPosition: PropTypes.oneOf(['top', 'bottom']),
  infoCta: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
  }),
  infoIcon: PropTypes.shape({
    path: PropTypes.string,
    alt: PropTypes.string,
  }),
  items: PropTypes.arrayOf(PropTypes.object),
  cta: PropTypes.shape({
    label: PropTypes.string,
    link: PropTypes.string,
    variant: PropTypes.string,
    color: PropTypes.string,
    ctaStyle: PropTypes.string,
    ctaTheme: PropTypes.string,
  }),
  noOfColumns: PropTypes.oneOf([1, 2, 3, 4, GRID_2X2PLUS1]),
  textPosition: PropTypes.oneOf(['center', 'left']),
  listProducts: PropTypes.bool,
  theme: PropTypes.string,
  type: PropTypes.oneOf([
    'text',
    'icon',
    'noIcon',
    'illustration',
    'leftCheckIcon',
    'thumbnail',
  ]),
  mobileCarousel: PropTypes.bool,
  enableAnimations: PropTypes.bool,
};

export default BenefitSection;
